<template>
    <div @click="toggleChat(false)" class="faq-page">
        <div class="row">
            <div class="faq-list">
                <div class="listed-faqs">
                    <h1 class="faq-headline">Frequently Asked Questions</h1>
                    <div v-for="(faq, i) in faqs" :key="i" class="display-faqs">
                        <details>
                            <summary>
                                {{ faq.question }}
                            </summary>
                            <p class="mt-2 text-gray-700 whitespace-pre-line" v-html="faq.answer">
                            </p>
                        </details>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div @click="toggleChat(false)" id="contact">
        <ContactView></ContactView>
    </div>
    <button v-if="!isOpen" @click="toggleChat(true)" class="chat-button grow">
        💬
    </button>

    <!-- Chatbot Window -->
    <div class="chat-window grow" v-if="isOpen">
        <div class="d-flex justify-content-between chat-header">
            <span>So-Well Prepared Tax Prep</span>
            <ChevronsDown class="text-white" @click="toggleChat(false)" />
        </div>

        <div class="chat-messages" ref="chatMessages">
            <div v-for="(msg, i) in messages" :key="i" class="mb-2">
                <p class="message" :class="msg.type === 'user'
                    ? 'user-message'
                    : 'bot-message'" v-html="sanitizeHTML(msg.text)">
                </p>
            </div>

            <div v-if="sendingMessage" class="spinner-grow text-secondary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <div class="faq-categories">
                <div v-if="!sendingMessage && !selectedCategory" class="categories">
                    <button v-for="cat in categories" :key="cat.id" @click="selectCategory(cat)">
                        {{ cat.category }}
                    </button>
                </div>

                <div v-if="selectedCategory" class="faq-questions">
                    <button v-for="q in filteredFaqs" :key="q.question" @click="sendMessage(q.question)">
                        {{ q.question }}
                    </button>
                </div>
            </div>
        </div>

        <div class="chat-footer">
            <input v-model="userInput" @keyup.enter="sendMessage(this.userInput)" :disabled="sendingMessage"
                class="input-send-message" placeholder="Type a message..." />
            <SendHorizontal @click="sendMessage(this.userInput)" class="text-white" :disabled="sendingMessage" />
        </div>
    </div>
</template>
  
<script>
import ContactView from '@/views/ContactView.vue';
import { ChevronsDown } from 'lucide-vue-next';
import { SendHorizontal } from 'lucide-vue-next';
import { sendLexMessage } from '@/api/backend'
import DOMPurify from 'dompurify';

export default {
    name: "FaqView",
    components: {
        ContactView,
        ChevronsDown,
        SendHorizontal
    },
    data() {
        return {
            isOpen: false,
            userInput: "",
            sendingMessage: false,
            selectedCategory: null,
            messages: [{ type: "bot", text: "Hi! How can I help you?" }],
            categories: [
                { category: "Income & Deductions", id: 1 },
                { category: "Tax Credits & Deductions", id: 2 },
                { category: "Problems & Penalties", id: 3 },
            ],
            faqs: [
                // --- Income & Deductions ---
                {
                    question: "Can I deduct my home office?",
                    answer: "Yes — but only if you're self-employed and the space is used regularly and exclusively for business. \nW-2 employees cannot deduct home office expenses due to current tax laws.",
                    category: "Income & Deductions",
                },
                {
                    question: "Is unemployment income taxable?",
                    answer: "Yes. Unemployment compensation is fully taxable at the federal level and may be taxable in some states.",
                    category: "Income & Deductions",
                },
                {
                    question: "I have side income — do I need to report it?",
                    answer: "Yes. All income must be reported, including freelance work, tips, or cash gigs — even if you don’t receive a 1099 form. This income is also subject to self-employment tax.",
                    category: "Income & Deductions",
                },

                // --- Tax Credits & Deductions ---
                {
                    question: "What tax credits am I eligible for?",
                    answer: "You may qualify for:   <ul>   <li>Earned Income Tax Credit (EITC)</li>   <li>Child Tax Credit</li>   <li>American Opportunity Credit</li>   <li>Lifetime Learning Credit</li>   <li>Saver's Credit</li>   <li>Premium Tax Credit</li> </ul> Eligibility is based on income, dependents, education, and other factors.",
                    category: "Tax Credits & Deductions",
                },
                {
                    question: "What's the difference between a deduction and a credit?",
                    answer: "Deductions reduce your taxable income (e.g., mortgage interest).\nCredits reduce your tax owed, dollar-for-dollar (e.g., Child Tax Credit).",
                    category: "Tax Credits & Deductions",
                },
                {
                    question: "When will I get my tax refund?",
                    answer: "Most refunds are issued within 21 days if you file electronically and use direct deposit.",
                    category: "Tax Credits & Deductions",
                },
                {
                    question: "Should I itemize or take the standard deduction?",
                    answer: "Most taxpayers take the standard deduction ($14,600 for single filers in 2024). You should only itemize if your total deductions (mortgage interest, state taxes, medical expenses, etc.) are higher than your standard deduction.",
                    category: "Tax Credits & Deductions",
                },
                {
                    question: "Can I file my taxes for free?",
                    answer: "Yes! If your income qualifies, you can use the IRS Free File program or free versions of major tax software.",
                    category: "Tax Credits & Deductions",
                },

                // --- Problems & Penalties ---
                {
                    question: "What if I can’t afford to pay my taxes?",
                    answer: "You should still file on time to avoid penalties. The IRS offers:   <ul>   <li>Monthly payment plans</li>   <li>Offer in Compromise (in limited cases)</li>   <li>Temporary hardship relief</li> </ul> Interest continues to accrue until paid in full.",
                    category: "Problems & Penalties",
                },
                {
                    question: "What are the penalties for filing or paying late?",
                    answer: "You may face the following penalties:   <ul>   <li>Failure to file: 5% per month (up to 25%)</li>   <li>Failure to pay: 0.5% per month</li>   </ul> Interest accrues daily on any unpaid balance",
                    category: "Problems & Penalties",
                },
                {
                    question: "Do I need to file state taxes too?",
                    answer: "Yes — if your state has income tax. A few states (like Florida and Texas) don’t require a return, but most do.",
                    category: "Problems & Penalties",
                },
                {
                    question: "Are cryptocurrencies taxable?",
                    answer: "Yes. The IRS treats cryptocurrency as property, so selling, trading, or using it for purchases is a taxable event.",
                    category: "Problems & Penalties",
                },
                {
                    question: "How do I claim my dependents?",
                    answer: "You can claim a child or relative if you: <ul>   <li>Provide more than 50% of their support</li>   <li>They live with you (generally)</li>   <li>They meet IRS rules for dependents</li>   <li>They don’t file a joint return (unless only to claim a refund)</li> </ul>",
                    category: "Problems & Penalties",
                },
                {
                    question: "What if I received a letter from the IRS?",
                    answer: "Don’t panic — and don’t ignore it. Bring the notice to your tax preparer or accountant so they can review it and help you respond appropriately.",
                    category: "Problems & Penalties",
                },
                {
                    question: "How long should I keep my tax records?",
                    answer: "Keep records for at least 3 years. If you're claiming losses or have complex returns, hold onto them for 7 years.",
                    category: "Problems & Penalties",
                },
                {
                    question: "Who is responsible if there’s a mistake on my tax return?",
                    answer: "While a tax professional may assist you, the IRS holds you, the taxpayer, ultimately responsible. If your preparer makes an error, they should work with you to correct it and communicate with the IRS on your behalf.",
                    category: "Problems & Penalties",
                },
                {
                    question: "Where can I find the tax resources?",
                    answer: `You can find the resources here: 
                        <ul>
                        <li><a href="https://www.irs.gov/taxtopics/tc509" target="_blank">IRS Home Office Deduction</a></li>
                        <li><a href="https://www.irs.gov/individuals/employees/unemployment-compensation" target="_blank">IRS Unemployment Compensation</a></li>
                        <li><a href="https://www.irs.gov/newsroom/all-income-is-taxable-including-gig-economy-and-tip-income" target="_blank">All Gig, Freelance & Side Income Taxable</a></li>
                        <li><a href="https://www.irs.gov/credits-and-deductions-for-individuals" target="_blank">IRS Tax Credits Overview</a></li>
                        <li><a href="https://www.irs.gov/newsroom/deductions-for-individuals-the-difference-between-standard-and-itemized-deductions-and-what-they-mean" target="_blank">IRS Standard vs. Itemized Deductions</a></li>
                        <li><a href="https://www.irs.gov/file-your-taxes-for-free" target="_blank">IRS Free File</a></li>
                        <li><a href="https://www.irs.gov/faqs/irs-procedures/collection-procedural-questions/collection-procedural-questions-3" target="_blank">IRS Penalties for Late Filing or Payment</a></li>
                        <li><a href="https://www.irs.gov/newsroom/what-taxpayers-should-do-if-they-receive-mail-from-the-irs" target="_blank">IRS Guidance for Tax Notice or Letter</a></li>
                        <li><a href="https://www.irs.gov/businesses/small-businesses-self-employed/how-long-should-i-keep-records" target="_blank">IRS Tax Record Retention</a></li>
                        <li><a href="https://www.irs.gov/credits-deductions/individuals/dependents" target="_blank">IRS Dependents Rules</a></li>
                        <li><a href="https://www.irs.gov/filing/digital-assets" target="_blank">IRS Digital Assets (Cryptocurrency)</a></li>
                        </ul>`,
                    category: "Tax Resources",
                }
            ],
        }
    },
    computed: {
        filteredFaqs() {
            return this.faqs.filter(
                (f) => f.category === this.selectedCategory.category
            );
        },
    },
    methods: {
        toggleChat(isOpenStatus) {
            this.isOpen = isOpenStatus;
            if (this.isOpen) {
                this.$nextTick(() => {
                    const chat = this.$refs.chatMessages;
                    chat.scrollTop = chat.scrollHeight;
                    this.sendingMessage = false
                });
            }
        },
        selectCategory(category) {
            this.selectedCategory = category;
            console.log(this.selectedCategory)
        },
        sendFaq(question) {
            this.sendMessage(question);
            this.selectedCategory = null; // hide FAQ questions
        },
        async sendMessage(message) {
            if (!message) return;
            if (this.sendingMessage) return;
            // Add user message to chat
            this.sendingMessage = true
            this.selectedCategory = null;
            this.userInput = ""
            this.messages.push({ type: "user", text: message });

            try {
                const botMessage = await sendLexMessage(message);
                this.messages.push({ type: "bot", text: botMessage });
                this.$nextTick(() => {
                    const chat = this.$refs.chatMessages;
                    chat.scrollTop = chat.scrollHeight;
                    this.sendingMessage = false
                });
            } catch (err) {
                console.error(err);
                this.messages.push({ type: "bot", text: "Error contacting bot." });
                this.sendingMessage = false
            }
        },
        wait(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        sanitizeHTML(text) {
            let cleaned_text = DOMPurify.sanitize(text)
            return cleaned_text
        }
    },
};
</script>

<style>
a {
    color: white
}

.faq-page {
    background-color: rgba(0, 0, 0, 0.15);
    padding: 2.5% 7.5%;
}

#contact {
    background-color: rgba(0, 0, 0, 0.25);
}

.faq-headline {
    color: white;
    font-size: 2rem;
    text-align: center
}

.display-faqs {
    color: #f5f2f2;
    text-align: left;
    letter-spacing: 0.5px;
    padding: 0 5%;
    margin-bottom: 0.5rem
}

.listed-faqs {
    background: #364248;
    padding: 20px;
    border-radius: 15px;
    box-shadow: rgb(255, 255, 255, 0.2) 0px 5px 15px;
}

.display-faqs summary {
    font-size: 1.25rem;
    font-weight: 700;
}

.display-faqs p {
    font-size: 1.1rem;
}

.chat-button {
    position: fixed;
    bottom: 5%;
    right: 2.5%;
    padding: 9px 15px;
    background-color: #2563eb;
    color: white;
    border: none;
    border-radius: 50px;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    transform: scale(0);
    /* start small */
    animation: grow 1s ease forwards;
}

.chat-button:hover {
    background-color: #1d4ed8;
}

.chat-window {
    position: fixed;
    bottom: 5%;
    right: 2.5%;
    width: 275px;
    height: 370px;
    border-radius: 12.5px;
    z-index: 1000;
    transform: scale(0);
    /* start small */
    animation: grow 0.75s ease forwards;
    /* play animation when added */
}

@keyframes grow {
    from {
        transform: scale(0);
    }

    to {
        transform: scale(1);
    }
}

.chat-window.hide {
    animation: shrink 0.3s ease forwards;
}

.chat-header {
    color: white;
    padding: 9px 15px;
    height: 10%;
    border-radius: 12.5px;
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
    background-color: #2563eb;
}

.chat-messages {
    background-color: white;
    color: white;
    padding: 9px 15px;
    overflow-y: auto;
    height: 80%;
    font-size: 0.9rem;
}

.chat-messages ul li {
    font-size: 0.9rem;
}

.user-message {
    display: flex;
    align-self: flex-start;
    background-color: #1d4ed8;
}

.bot-message {
    background-color: #8c8c8e;
    margin-left: 10%;
}

.message {
    width: 90%;
    padding: 0.25rem 0.7rem;
    border-radius: 5px
}

.input-send-message {
    background-color: transparent;
    padding-left: 5%;
    width: 85%;
    padding-right: 5%;
    border-color: transparent;
    color: white;
}

.input-send-message:focus {
    border-color: transparent;
    outline: none;
    box-shadow: none;
}

.chat-footer {
    background-color: black;
    height: 10%;
    border-bottom-left-radius: 12.5px;
    border-bottom-right-radius: 12.5px;
}

.faq-categories button {
    background-color: #34C759;
    font-size: 0.85rem;
    color: white;
    width: 100%;
    border-radius: 10px;
    border-color: transparent;
    margin-bottom: 0.5rem;
    padding: 0.1rem 0.25rem;
    outline: none;
    box-shadow: none;
}

.faq-categories button:hover {
    background-color: #40f96e;
}

.faq-categories button:focus {
    outline: none;
    box-shadow: none;
}

.spinner-grow {
    margin-left: 85%;
}

@media(max-width: 792px) and (min-width: 576px) {
    .faq-headline {
        font-size: 1.75rem;
    }

    .display-faqs {
        letter-spacing: 0.45px;
        padding: 0 5%;
        margin-bottom: 0.45rem
    }

    .listed-faqs {
        padding: 15px;
        border-radius: 15px
    }

    .display-faqs summary {
        font-size: 1.15rem;
        font-weight: 700;
    }

    .display-faqs p {
        font-size: 1rem;
    }
}

@media(max-width: 576px) and (min-width: 420px) {
    .faq-headline {
        font-size: 1.45rem;
    }

    .display-faqs {
        letter-spacing: 0.45px;
        padding: 0 5%;
        margin-bottom: 0.45rem
    }

    .listed-faqs {
        padding: 15px;
        border-radius: 15px
    }

    .display-faqs summary {
        font-size: 1.1rem;
        font-weight: 700;
    }

    .display-faqs p {
        font-size: 0.95rem;
    }

    .chat-button {
        position: fixed;
        bottom: 2.5%;
        right: 3.5%;
        padding: 9px 15px;
    }

    .chat-button:hover {
        background-color: #1d4ed8;
    }

    .chat-window {
        width: 400px;
        height: 760px;
        bottom: 0%;
        right: 3.5%;
    }

    .chat-header {
        color: white;
        padding: 9px 15px;
        height: 5%;
        display: flex;
        align-items: center;
    }

    .chat-messages {
        height: 90%;
        font-size: 0.95rem;
    }

    .chat-messages ul li {
        font-size: 0.95rem;
    }

    .chat-footer {
        height: 5%;
        padding-top: 1.75px;
    }

    .input-send-message {
        padding-left: 5%;
        width: 90%;
    }

    .spinner-grow {
        margin-left: 85%;
    }

    .faq-page {
        padding: 7.5% 7.5%;
    }

    .chat-footer {
        display: none;
    }

    .chat-messages {
        border-radius: 12.5px;
        border-top-left-radius: 0px;
        border-top-right-radius: 0px;
    }
}

@media(max-width: 420px) and (min-width: 365px) {
    .faq-headline {
        font-size: 1.2rem;
    }

    .display-faqs {
        letter-spacing: 0.45px;
        padding: 0 5%;
        margin-bottom: 0.45rem
    }

    .listed-faqs {
        padding: 15px;
        border-radius: 15px
    }

    .display-faqs summary {
        font-size: 1rem;
        font-weight: 700;
    }

    .display-faqs p {
        font-size: 0.85rem;
    }

    .chat-button {
        position: fixed;
        bottom: 2.5%;
        right: 3%;
        padding: 9px 15px;
    }

    .chat-window {
        width: 345px;
        height: 680px;
        bottom: 0%;
        right: 3%;
    }

    .chat-header {
        color: white;
        padding: 9px 15px;
        height: 5%;
        display: flex;
        align-items: center;
    }

    .chat-messages {
        height: 90%;
        font-size: 0.95rem;
    }

    .chat-messages ul li {
        font-size: 0.95rem;
    }

    .chat-footer {
        height: 5%;
        padding-top: 1.75px;
    }

    .input-send-message {
        padding-left: 5%;
        width: 90%;
    }

    .spinner-grow {
        margin-left: 85%;
    }

    .faq-page {
        padding: 7.5% 7.5%;
    }

    .chat-footer {
        display: none;
    }

    .chat-messages {
        border-radius: 12.5px;
        border-top-left-radius: 0px;
        border-top-right-radius: 0px;
    }
}

@media(max-width: 360px) {
    .faq-list {
        padding: 0 5%
    }

    .faq-headline {
        font-size: 1.1rem;
    }

    .display-faqs {
        letter-spacing: 0.4px;
        padding: 0 4%;
        margin-bottom: 0.35rem
    }

    .listed-faqs {
        padding: 12.5px;
        border-radius: 12.5px
    }

    .display-faqs summary {
        font-size: 0.9rem;
        font-weight: 700;
    }

    .display-faqs p {
        font-size: 0.8rem;
    }

    .chat-button {
        position: fixed;
        bottom: 2.5%;
        right: 3%;
        padding: 9px 15px;
    }

    .chat-window {
        width: 300px;
        height: 600px;
        bottom: 0%;
        right: 3%;
    }

    .chat-header {
        color: white;
        padding: 9px 15px;
        height: 6.5%;
        display: flex;
        align-items: center;
    }

    .chat-messages {
        height: 87%;
        font-size: 0.95rem;
    }

    .chat-messages ul li {
        font-size: 0.95rem;
    }

    .chat-footer {
        height: 6.5%;
        padding-top: 1.75px;
    }

    .input-send-message {
        padding-left: 5%;
        width: 90%;
    }

    .spinner-grow {
        margin-left: 85%;
    }

    .faq-page {
        padding: 7.5% 7.5%;
    }

    .chat-footer {
        display: none;
    }

    .chat-messages {
        border-radius: 12.5px;
        border-top-left-radius: 0px;
        border-top-right-radius: 0px;
    }
}
</style>